import { adminService } from "@/api/admin/admin.service";
import { TAdminUser, TCreateUserRequest } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { useNavigate } from "@tanstack/react-router";
import {
  Ban,
  Eye,
  Loader2,
  LockOpen,
  MailCheck,
  PhoneCall,
  Trash2,
  UserCheck,
} from "lucide-react";
import { useState } from "react";

const EMPTY_CREATE_FORM: TCreateUserRequest = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  phone_number: "",
};

const PER_PAGE = 10;

function VerificationBadges({ user }: { user: TAdminUser }) {
  return (
    <div className="flex flex-wrap gap-1">
      <Badge
        variant="outline"
        className={
          user.email_verified
            ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
            : "border-border bg-muted text-muted-foreground"
        }
      >
        {user.email_verified ? "Email ✓" : "Email ✗"}
      </Badge>
      <Badge
        variant="outline"
        className={
          user.phone_verified
            ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
            : "border-border bg-muted text-muted-foreground"
        }
      >
        {user.phone_verified ? "Phone ✓" : "Phone ✗"}
      </Badge>
    </div>
  );
}

export function UserManagementPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((t) => t + 1);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<TCreateUserRequest>(EMPTY_CREATE_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<TAdminUser | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TAdminUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetcher = async (query: TDataGridQuery) => {
    const statusFilter = query.filters.status_id;
    const response = await adminService.getUsers(
      {
        page: query.page,
        page_size: query.per_page,
        ...(query.search ? { search: query.search } : {}),
        ...(typeof statusFilter === "string" && statusFilter
          ? { status_id: Number(statusFilter) }
          : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const columns: TColumnDef<TAdminUser>[] = [
    {
      id: "user",
      header: "User",
      type: "user",
      sortable: true,
      sortKey: "first_name",
      accessor: (u) => ({ name: `${u.first_name} ${u.last_name}`.trim(), email: u.email }),
    },
    {
      id: "status_id",
      header: "Status",
      sortable: true,
      sortKey: "status_id",
      cell: (u) => (
        <Badge
          variant="outline"
          className={
            u.status_id === 1
              ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
              : "border-destructive/30 bg-destructive/10 text-destructive"
          }
        >
          {u.status_id === 1 ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "verification",
      header: "Verification",
      cell: (u) => <VerificationBadges user={u} />,
    },
    {
      id: "companies_count",
      header: "Companies",
      align: "right",
      accessor: (u) => u.companies_count ?? 0,
    },
    {
      id: "chats_count",
      header: "Chats",
      align: "right",
      accessor: (u) => u.chats_count ?? 0,
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    {
      id: "status_id",
      type: "select",
      label: "Status",
      options: [
        { label: "Active", value: "1" },
        { label: "Inactive", value: "2" },
      ],
    },
  ];

  const handleViewUser = (userId: string) => {
    void navigate({ to: "/app/admin/users/$user_id", params: { user_id: userId } });
  };

  const runAction = async (
    request: () => ReturnType<typeof adminService.activateUser>,
    successTitle: string
  ) => {
    const response = await request();
    if (response.error) {
      toast({ variant: "destructive", title: await extractErrorMessage(response.error) });
    } else {
      toast({ title: successTitle });
      reload();
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateTarget) return;
    setIsDeactivating(true);
    const response = await adminService.deactivateUser(deactivateTarget.id);
    if (response.error) {
      toast({ variant: "destructive", title: await extractErrorMessage(response.error) });
    } else {
      toast({ title: "User deactivated" });
      reload();
    }
    setIsDeactivating(false);
    setDeactivateTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const response = await adminService.deleteUser(deleteTarget.id);
    if (response.error) {
      toast({ variant: "destructive", title: await extractErrorMessage(response.error) });
    } else {
      toast({ title: "User deleted" });
      reload();
    }
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const handleCreateUser = async () => {
    setIsCreating(true);
    const response = await adminService.createUser(createForm);
    if (response.error) {
      toast({ variant: "destructive", title: await extractErrorMessage(response.error) });
    } else {
      toast({ title: "User created successfully" });
      setIsCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      reload();
    }
    setIsCreating(false);
  };

  const isCreateFormValid =
    createForm.first_name.trim() !== "" &&
    createForm.last_name.trim() !== "" &&
    createForm.email.trim() !== "" &&
    createForm.password !== "" &&
    createForm.phone_number.trim() !== "";

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Users</h1>
            <p className="mt-2 text-sm text-muted-foreground">Here you will find all the users registered.</p>
          </div>
        </div>

        <DataGrid<TAdminUser>
          getRowId={(row) => row.id}
          persistKey="admin.users"
          ariaLabel="Users"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          selection
          pagination={{ mode: "page", pageSize: PER_PAGE, pageSizeOptions: [10, 20, 50] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name or email",
            create: { label: "Create User", onClick: () => setIsCreateOpen(true) },
          }}
          rowActions={(user) => [
            {
              label: "View",
              icon: <Eye className="h-4 w-4" />,
              onClick: () => handleViewUser(user.id),
            },
            ...(user.status_id === 1
              ? [
                  {
                    label: "Deactivate",
                    icon: <Ban className="h-4 w-4" />,
                    variant: "destructive" as const,
                    onClick: () => setDeactivateTarget(user),
                  },
                ]
              : [
                  {
                    label: "Activate",
                    icon: <UserCheck className="h-4 w-4" />,
                    onClick: () =>
                      void runAction(() => adminService.activateUser(user.id), "User activated"),
                  },
                ]),
            {
              label: "Unlock",
              icon: <LockOpen className="h-4 w-4" />,
              onClick: () => void runAction(() => adminService.unlockUser(user.id), "User unlocked"),
            },
            {
              label: "Verify email",
              icon: <MailCheck className="h-4 w-4" />,
              onClick: () =>
                void runAction(() => adminService.verifyUserEmail(user.id), "Email verified"),
            },
            {
              label: "Verify phone",
              icon: <PhoneCall className="h-4 w-4" />,
              onClick: () =>
                void runAction(() => adminService.verifyUserPhone(user.id), "Phone verified"),
            },
            {
              label: "Delete",
              icon: <Trash2 className="h-4 w-4" />,
              variant: "destructive",
              onClick: () => setDeleteTarget(user),
            },
          ]}
        />
      </div>

      {/* Create User Dialog */}
      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          if (!open && !isCreating) {
            setIsCreateOpen(false);
            setCreateForm(EMPTY_CREATE_FORM);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Create a new user account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="create-user-first-name"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  First Name
                </label>
                <Input
                  id="create-user-first-name"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))}
                  placeholder="Jane"
                />
              </div>
              <div>
                <label
                  htmlFor="create-user-last-name"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Last Name
                </label>
                <Input
                  id="create-user-last-name"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="create-user-email"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Email
              </label>
              <Input
                id="create-user-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <label
                htmlFor="create-user-phone"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Phone Number
              </label>
              <Input
                id="create-user-phone"
                type="tel"
                value={createForm.phone_number}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone_number: e.target.value }))}
                placeholder="+254712345678"
              />
            </div>
            <div>
              <label
                htmlFor="create-user-password"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Password
              </label>
              <Input
                id="create-user-password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateOpen(false);
                setCreateForm(EMPTY_CREATE_FORM);
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreateUser()}
              disabled={!isCreateFormValid || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isDeactivating) setDeactivateTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate User</DialogTitle>
            <DialogDescription>
              {deactivateTarget
                ? `Are you sure you want to deactivate ${deactivateTarget.first_name} ${deactivateTarget.last_name}? They will no longer be able to sign in.`
                : "Are you sure you want to deactivate this user?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeactivateTarget(null)}
              disabled={isDeactivating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeactivateConfirm()}
              disabled={isDeactivating}
            >
              {isDeactivating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deactivating…
                </>
              ) : (
                "Deactivate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `Are you sure you want to permanently delete ${deleteTarget.first_name} ${deleteTarget.last_name}? This action cannot be undone.`
                : "Are you sure you want to delete this user?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
